import { btn } from "@/components/ds/styles";
import { Link } from "@tanstack/react-router";
import { GraduationCap, Play } from "lucide-react";
import { useState } from "react";
import type { HomeSituation } from "@/data/home-situations";
import { ageRange, firstRakiza, fillDuration } from "@/lib/home-format";
import { CHANNEL, INTRO_SHORT, SITUATION_SHORTS, TOP_SHORT, formatViewsAr, onThumbLoad, thumbRef, thumbUrl } from "@/data/home-videos";
import { DEGREES } from "@/data/credentials";
import { VideoCard } from "@/components/home/video-card";
import { useVideoPlayer } from "@/components/home/video-player";
import portrait from "@/assets/dr-sara-portrait.jpg.asset.json";
import headshot from "@/assets/dr-sara-headshot.jpg.asset.json";

export type HomePost = {
  slug: string;
  title_ar: string;
  reading_min: number | null;
  age_band: unknown;
  rakiza: unknown;
  og_image: string | null;
  image_alt: string | null;
};

export const DOCTORATE_LINE = `${DEGREES[0].title} – ${DEGREES[0].issuer}`;

const primaryBtn = btn.primary;
const textLink = btn.link;

/** Hero: chips + an always-visible answer area; desktop adds the arch portrait. */
export function SituationPicker({
  situations,
  posts,
  duration,
  selected,
  onSelect: setSelected,
  whatsappUrl,
}: {
  situations: HomeSituation[];
  posts: Record<string, HomePost>;
  duration: number | string;
  selected: string | null;
  onSelect: (slug: string | null) => void;
  whatsappUrl: string | null;
}) {
  const live = situations.filter((s) => posts[s.slug]);
  const sit = live.find((s) => s.slug === selected) ?? null;
  const post = sit ? posts[sit.slug] : null;
  const short = sit ? SITUATION_SHORTS[sit.slug] : null;

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-start lg:gap-14">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <img
            src={headshot.url}
            alt="د. سارة النحّاس"
            width={400}
            height={400}
            loading="lazy"
            decoding="async"
            className="h-10 w-10 shrink-0 rounded-full object-cover object-center ring-2 ring-amber-500 lg:hidden"
          />
          <p className="font-display text-[13px] font-bold text-amber-800">د. سارة النحّاس · استشارية تربوية ونفسية</p>
        </div>
        <h1 className="mt-3 font-display text-[34px] font-extrabold leading-[1.25] text-maroon-500 [text-wrap:balance] lg:text-[58px] lg:leading-[1.2]">
          شو اللي <span className="mark whitespace-nowrap">شاغل بالك</span> اليوم؟
        </h1>
        <p className="mt-4 text-[17px] leading-[1.7] text-text-secondary lg:hidden">
          كبسة وحدة على اللي عم يصير معك، وبنوصلك لمقال وفيديو قصير وخطوة وحدة بعده.
        </p>
        <p className="mt-4 hidden text-[21px] leading-[1.7] text-text-secondary lg:block">
          موقف واحد بيكفّي: كبسة على اللي عم يصير معك، وبنوصلك لمقال بيسمّيه، وفيديو قصير من د. سارة، وخطوة واحدة بعده.
        </p>

        {live.length > 0 && (
          <div role="group" aria-label="اختيار الموقف" className="mt-6 flex flex-wrap gap-2.5">
            {live.map((s) => {
              const on = s.slug === selected;
              return (
                <button
                  key={s.slug}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setSelected(on ? null : s.slug)}
                  className={
                    "min-h-12 rounded-ds-pill border px-5 font-display text-[15px] font-bold transition-colors duration-ds-base ease-ds motion-reduce:transition-none lg:min-h-[52px] lg:text-[17px] " +
                    (on
                      ? "border-amber-500 bg-amber-500 text-brown-700"
                      : "border-hairline-default bg-surface text-text-primary hover:bg-cream-50")
                  }
                >
                  {s.chip}
                </button>
              );
            })}
          </div>
        )}

        <div aria-live="polite" className="mt-6">
          <div key={sit?.slug ?? "intro"} className="ds-rise">
            {sit && post && short ? (
              <div className="rounded-ds-xl border border-hairline-subtle bg-surface p-4 shadow-sm lg:p-5">
                <div className="flex gap-4">
                  <VideoCard video={short} label="فيديو قصير" className="h-[176px] w-[112px] lg:h-[236px] lg:w-[156px]" titleSize="text-[13px] lg:text-[14px]" />
                  <Link to="/blog/$slug" params={{ slug: post.slug }} className="flex min-w-0 flex-1 flex-col">
                    <span className="font-display text-[13px] font-bold text-amber-800">مقال لحالتك اليوم</span>
                    <span className="mt-1.5 line-clamp-4 font-display text-[17px] font-extrabold leading-snug text-text-primary lg:text-[22px]">
                      {post.title_ar}
                    </span>
                    <span className="mt-2 text-[13px] text-text-secondary">
                      {[post.reading_min ? `${post.reading_min} دقيقة قراءة` : null, firstRakiza(post.rakiza)].filter(Boolean).join(" · ")}
                      {ageRange(post.age_band) && (
                        <>
                          {" · "}
                          <bdi dir="ltr">{ageRange(post.age_band)}</bdi> سنوات
                        </>
                      )}
                    </span>
                    <span className="mt-auto inline-flex min-h-11 items-end pt-2 font-display text-[15px] font-bold text-text-link">قراءة المقال ←</span>
                  </Link>
                </div>
                <div className="mt-4 rounded-ds-lg bg-cream-50 p-4">
                  <p className="font-display text-[16px] font-bold leading-[1.6] text-text-primary">{sit.line}</p>
                  <p className="mt-2 text-[14px] text-text-secondary">{fillDuration(sit.hook, duration)}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                    <Link
                      to="/book"
                      className="inline-flex min-h-12 items-center rounded-ds-pill bg-maroon-100 px-5 font-display text-[15px] font-bold text-maroon-500 transition-colors duration-ds-fast hover:bg-cream-300"
                    >
                      حجز استشارة ←
                    </Link>
                    {whatsappUrl && (
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className={textLink}>
                        سؤال على واتساب ←
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex gap-4 rounded-ds-xl border border-hairline-subtle bg-surface p-4 shadow-sm">
                <VideoCard video={INTRO_SHORT} label="د. سارة" className="h-[176px] w-[112px]" titleSize="text-[13px]" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <p className="font-display text-[19px] font-extrabold text-text-primary">تعرّف على د. سارة</p>
                  <p className="mt-1.5 text-[15px] leading-[1.7] text-text-secondary">من تطوير الذات للدكتوراه، بدقيقة وحدة</p>
                  <p className="mt-auto text-[13px] text-text-secondary">أو كبسة على موقف من فوق</p>
                  <Link to="/blog" className={textLink}>كل المقالات ←</Link>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-7 hidden lg:block">
          <Link to="/book" className={primaryBtn}>حجز استشارة أونلاين ←</Link>
          <p className="mt-4 text-[15px] text-text-secondary">
            ولا واحد منهم؟{" "}
            <Link to="/blog" className={textLink}>كل المقالات ←</Link>
          </p>
        </div>
      </div>

      <HeroArch />
    </div>
  );
}

function HeroArch() {
  const { play } = useVideoPlayer();
  const [fallback, setFallback] = useState(false);
  return (
    <div className="relative mx-auto hidden w-full max-w-[420px] pt-6 lg:block">
      <span aria-hidden className="absolute end-0 top-0 h-[300px] w-[300px] rounded-full border border-amber-500/60" />
      <span aria-hidden className="absolute -start-8 bottom-24 h-[220px] w-[220px] rounded-full border border-amber-500/50" />
      <div className="relative overflow-hidden rounded-b-ds-xl rounded-t-[220px] bg-maroon-100">
        <img
          src={portrait.url}
          alt="د. سارة النحّاس"
          width={600}
          height={1066}
          fetchPriority="high"
          decoding="async"
          className="aspect-[420/560] w-full object-cover object-top mix-blend-multiply"
        />
      </div>
      <p className="absolute start-[-28px] top-16 rounded-ds-pill bg-surface px-4 py-2 font-display text-[14px] font-bold text-text-primary shadow-md">
        <bdi dir="ltr">{Math.round(CHANNEL.subscribers / 1000)}</bdi> ألف مشترك على يوتيوب
      </p>
      <button
        type="button"
        onClick={() => play(TOP_SHORT)}
        aria-label={`تشغيل الفيديو: ${TOP_SHORT.title}`}
        className="lift absolute -start-12 bottom-28 flex w-[250px] items-center gap-3 rounded-ds-lg bg-surface p-2.5 text-start shadow-md"
      >
        <span className="relative h-[72px] w-[48px] shrink-0 overflow-hidden rounded-[12px] bg-ink">
          <img
            src={thumbUrl(TOP_SHORT, fallback)}
            ref={thumbRef} onLoad={onThumbLoad}
            onError={() => setFallback(true)}
            alt=""
            loading="lazy"
            width={48}
            height={72}
            className="h-full w-full object-cover"
          />
          <Play className="absolute inset-0 m-auto h-5 w-5 fill-cream-50 text-cream-50" strokeWidth={1.75} aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="line-clamp-2 font-display text-[13px] font-bold leading-snug text-text-primary">{TOP_SHORT.title}</span>
          <span className="mt-1 block text-[13px] text-text-secondary">الأكثر مشاهدة · {formatViewsAr(TOP_SHORT.views)} · تشغيل هون</span>
        </span>
      </button>
      <p className="absolute -end-4 bottom-6 inline-flex items-center gap-2 rounded-ds-pill bg-teal-700 px-4 py-2 font-display text-[14px] font-bold text-cream-50 shadow-md">
        <GraduationCap className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        {DOCTORATE_LINE}
      </p>
    </div>
  );
}
